import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { ApiError } from "../api/client";
import { checkInvite, registerWithInvite } from "../api/auth";
import {
  MIN_PASSWORD_LENGTH,
  canSubmitJoin,
  inviteCheckPhaseFromResponse,
  messageForJoinError,
  messageForMissingCode,
  runJoinSubmit,
  signedInJoinWarning,
  type InviteCheckPhase,
  type JoinSubmitPhase,
} from "../lib/joinInvite";

// /join?code=... — the redeem-side counterpart to InvitesPage.tsx's minting
// UI, and the URL the beta-invite email links to (see
// workers/auth/src/lib/emailTemplates.ts's inviteEmail + JOIN_URL_BASE in
// workers/auth/src/routes/invites.ts). Standalone unauthenticated route
// (like /login), not RequireAuth-gated — redemption is registration, not a
// thing you need an existing session for. See lib/joinInvite.ts's header
// comment for why there's no "existing account joins" state.
export function JoinPage() {
  const { api, state, setSession } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const code = searchParams.get("code");

  const [check, setCheck] = useState<InviteCheckPhase>({ status: "checking" });
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [submit, setSubmit] = useState<JoinSubmitPhase>({ status: "editing" });
  const [manualCode, setManualCode] = useState("");

  useEffect(() => {
    if (!code) {
      setCheck({ status: "error", message: messageForMissingCode() });
      return;
    }
    let cancelled = false;
    checkInvite(api, code)
      .then((meta) => {
        if (!cancelled) setCheck(inviteCheckPhaseFromResponse(meta));
      })
      .catch((err) => {
        if (cancelled) return;
        setCheck({
          status: "error",
          message: err instanceof ApiError ? messageForJoinError(err) : "Couldn't check this invite. Please try again.",
        });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const busy = submit.status === "submitting";
  const currentEmail = state.status === "authenticated" ? state.account?.email ?? null : null;
  const warning = signedInJoinWarning(currentEmail);
  const returnState = { from: { pathname: location.pathname, search: location.search } };

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy || check.status !== "ready" || !code) return;
    setSubmit({ status: "submitting" });
    const result = await runJoinSubmit({ email, displayName, password }, async () => {
      const res = await registerWithInvite(api, { code, email, display_name: displayName, password });
      setSession({ access_token: res.access_token, refresh_token: res.refresh_token, account: res.account });
    });
    setSubmit(result);
  }

  if (check.status === "checking") {
    return (
      <div className="card center-card">
        <h1>Checking invite…</h1>
      </div>
    );
  }

  if (check.status === "error" && !code) {
    // No code in the URL at all — the email also prints the raw code as a
    // fallback for a broken/stripped link (see emailTemplates.ts's
    // inviteEmail), so give that fallback somewhere to go instead of
    // dead-ending here.
    return (
      <div className="card center-card">
        <h1>Enter your invite code</h1>
        <p className="meta">{check.message}</p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const trimmed = manualCode.trim();
            if (trimmed) setSearchParams({ code: trimmed });
          }}
        >
          <div className="field">
            <label htmlFor="manualCode">Invite code</label>
            <input id="manualCode" type="text" value={manualCode} autoFocus required
              onChange={(e) => setManualCode(e.target.value)} />
          </div>
          <button className="btn primary" type="submit" disabled={!manualCode.trim()}>Continue</button>
        </form>
      </div>
    );
  }

  if (check.status === "error") {
    return (
      <div className="card center-card">
        <h1>Invite unavailable</h1>
        <p className="meta">{check.message}</p>
        <Link className="btn primary" to="/login">Sign in</Link>
      </div>
    );
  }

  if (submit.status === "joined") {
    return (
      <div className="card center-card">
        <h1>You're in</h1>
        <p className="meta">Your Prism account has been created. Next, set up two-factor authentication.</p>
        <Link className="btn primary" to="/2fa/enroll">Continue</Link>
      </div>
    );
  }

  return (
    <div className="card center-card">
      <h1>Join Prism</h1>
      <p className="meta">You've been invited to join as <strong>{check.role}</strong>.</p>
      {warning && <p className="subtle">{warning}</p>}
      <form onSubmit={onSubmit}>
        <div className="field">
          <label htmlFor="email">Email</label>
          <input id="email" type="email" value={email} autoFocus required disabled={busy}
            onChange={(e) => setEmail(e.target.value)} />
          {check.emailPinned && (
            <p className="subtle">This invite is pinned to a specific email — use the one it was sent to.</p>
          )}
        </div>
        <div className="field">
          <label htmlFor="displayName">Display name</label>
          <input id="displayName" type="text" value={displayName} required disabled={busy}
            onChange={(e) => setDisplayName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="password">Password</label>
          <input id="password" type="password" value={password} required disabled={busy}
            minLength={MIN_PASSWORD_LENGTH}
            onChange={(e) => setPassword(e.target.value)} />
          <p className="subtle">At least {MIN_PASSWORD_LENGTH} characters.</p>
        </div>
        {submit.status === "failed" && (
          <div className="error">
            {submit.message}
            {submit.emailTaken && (
              <>
                {" "}
                <Link to="/login" state={returnState}>Sign in instead</Link>
              </>
            )}
          </div>
        )}
        <button className="btn primary" type="submit" disabled={busy || !canSubmitJoin({ email, displayName, password })}>
          {busy ? "Joining…" : "Join"}
        </button>
      </form>
    </div>
  );
}
